import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import registerServiceWorker from '../pwa/registerServiceWorker';

const PwaContext = createContext({
  isStandalone: false,
  isInstallable: false,
  shouldShowInstallBanner: false,
  isUpdateAvailable: false,
  installApp: async () => {},
  dismissInstallPrompt: () => {},
  applyUpdate: () => {}
});

const INSTALL_DISMISS_KEY = 'ims_pwa_install_dismissed_at';
const INSTALL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const readDismissedAt = () => {
  try {
    const value = window.localStorage.getItem(INSTALL_DISMISS_KEY);
    return value ? parseInt(value, 10) : 0;
  } catch {
    return 0;
  }
};

const writeDismissedAt = (timestamp) => {
  try {
    window.localStorage.setItem(INSTALL_DISMISS_KEY, String(timestamp));
  } catch {
    return;
  }
};

const getStandaloneState = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const PwaProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallPromptVisible, setInstallPromptVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(getStandaloneState());
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  const [isUpdateAvailable, setUpdateAvailable] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(readDismissedAt);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setInstallPromptVisible(true);
    };
    const handleAppInstalled = () => {
      setInstallPromptVisible(false);
      setDeferredPrompt(null);
      setDismissedAt(Date.now());
      setIsStandalone(true);
    };
    const handleDisplayModeChange = () => {
      setIsStandalone(getStandaloneState());
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.matchMedia('(display-mode: standalone)').addEventListener('change', handleDisplayModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  useEffect(() => {
    const config = {
      onUpdate: (registration) => {
        setWaitingRegistration(registration);
        setUpdateAvailable(true);
      },
      onSuccess: (registration) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Service Worker registered successfully:', registration);
        }
      },
      onError: (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Service Worker registration error:', error);
          const isSecure = window.isSecureContext !== false && 
            (window.location.protocol === 'https:' || 
             window.location.hostname === 'localhost' ||
             window.location.hostname === '127.0.0.1');
          if (!isSecure && window.location.protocol === 'http:') {
            console.warn(
              '⚠️ PWA Installation requires HTTPS when accessing via IP address. ' +
              'Service Workers and the install prompt will not work over HTTP (except localhost). ' +
              `Current URL: ${window.location.href}`
            );
          }
        }
      }
    };
    registerServiceWorker(config);
    let isReloadPending = false;
    const handleControllerChange = () => {
      if (isReloadPending) {
        return;
      }
      isReloadPending = true;
      window.location.reload();
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }
    return () => {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }
    setInstallPromptVisible(false);
    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    if (!choiceResult || choiceResult.outcome !== 'accepted') {
      const timestamp = Date.now();
      setDismissedAt(timestamp);
      writeDismissedAt(timestamp);
    }
  }, [deferredPrompt]);

  const dismissInstallPrompt = useCallback(() => {
    const timestamp = Date.now();
    setInstallPromptVisible(false);
    setDeferredPrompt(null);
    setDismissedAt(timestamp);
    writeDismissedAt(timestamp);
  }, []);

  const applyUpdate = useCallback(() => {
    if (waitingRegistration && waitingRegistration.waiting) {
      waitingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setUpdateAvailable(false);
  }, [waitingRegistration]);

  const isInstallable = Boolean(deferredPrompt);
  const shouldShowInstallBanner = isInstallable && !isStandalone && isInstallPromptVisible && Date.now() - dismissedAt > INSTALL_COOLDOWN_MS;

  const value = useMemo(
    () => ({
      isStandalone,
      isInstallable,
      shouldShowInstallBanner,
      isUpdateAvailable,
      installApp,
      dismissInstallPrompt,
      applyUpdate
    }),
    [isStandalone, isInstallable, shouldShowInstallBanner, isUpdateAvailable, installApp, dismissInstallPrompt, applyUpdate]
  );

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
};

export const usePwaContext = () => useContext(PwaContext);

